import { faBell } from '@fortawesome/pro-solid-svg-icons/faBell';
import { faBuilding } from '@fortawesome/pro-solid-svg-icons/faBuilding';
import { faChartBar } from '@fortawesome/pro-solid-svg-icons/faChartBar';
import { faChartLine } from '@fortawesome/pro-solid-svg-icons/faChartLine';
import { faCog } from '@fortawesome/pro-solid-svg-icons/faCog';
import { faFlag } from '@fortawesome/pro-solid-svg-icons/faFlag';
import { faFlask } from '@fortawesome/pro-solid-svg-icons/faFlask';
import { faKey } from '@fortawesome/pro-solid-svg-icons/faKey';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { faShield } from '@fortawesome/pro-solid-svg-icons/faShield';
import { faUser } from '@fortawesome/pro-solid-svg-icons/faUser';
import { faUserGear } from '@fortawesome/pro-solid-svg-icons/faUserGear';
import { faUsers } from '@fortawesome/pro-solid-svg-icons/faUsers';
import { NavigationConfig } from './types';
import { getNewAdminUrl } from '../../../utils/urls';
import { UserType } from '../../../../types';

interface SettingsNavConfigOptions {
  tenantId: string;
  user?: UserType | null;
  flags?: {
    enableMadapi?: boolean;
  };
}

export function getSettingsNavConfig({
  tenantId,
  user,
  flags = {},
}: SettingsNavConfigOptions): NavigationConfig {
  const isAdmin =
    user?.superKudu || user?.role === 'admin' || user?.role === 'architect';
  const isSuperKudu = user?.superKudu === true;
  const legacyAdminUrl = getNewAdminUrl();

  const sections: NavigationConfig['sections'] = [
    {
      label: 'MY SETTINGS',
      items: [
        {
          label: 'My Profile',
          href: `${legacyAdminUrl}/org/${tenantId}/settings/profile`,
          icon: faUser,
          visibleFor: 'always' as const,
          activeMatcher: (path: string) => path.includes('/settings/profile'),
          isExternal: true,
        },
        ...(flags.enableMadapi || isSuperKudu
          ? [
              {
                label: 'My API Keys',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/api-keys`,
                icon: faKey,
                visibleFor: 'always' as const,
                activeMatcher: (path: string) =>
                  path.includes('/settings/api-keys') ||
                  path.includes('/user-settings'),
                isExternal: true,
              },
            ]
          : []),
      ],
    },
    {
      label: 'ACCOUNT',
      items: [
        ...(isAdmin
          ? [
              {
                label: 'Account',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/account`,
                icon: faBuilding,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/account') &&
                  !path.includes('/settings/account/'),
                isExternal: true,
              },
              {
                label: 'Authentication',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/authentication`,
                icon: faLock,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/authentication'),
                isExternal: true,
              },
              {
                label: 'Users & Teams',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/users`,
                icon: faUsers,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/users'),
                isExternal: true,
              },
            ]
          : []),
        {
          label: 'Push Configuration',
          href: `/org/${tenantId}/push`,
          icon: faBell,
          visibleFor: 'always' as const,
          activeMatcher: (path: string) => path.includes('/push'),
          isExternal: false,
        },
        ...(isAdmin
          ? [
              {
                label: 'Processes',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/processes`,
                icon: faCog,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/processes'),
                isExternal: true,
              },
              {
                label: 'API Usage',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/usage`,
                icon: faChartLine,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/usage'),
                isExternal: true,
              },
              {
                label: 'Privacy',
                href: `/org/${tenantId}/settings/privacy`,
                icon: faShield,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/settings/privacy'),
                isExternal: false,
              },
            ]
          : []),
      ],
    },
    {
      label: 'ANALYSIS SETTINGS',
      items: [
        ...(isAdmin
          ? [
              {
                label: 'Personas',
                href: `${legacyAdminUrl}/org/${tenantId}/preferences/persona`,
                icon: faUsers,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/preferences/personas') ||
                  path.includes('/preferences/persona'),
                isExternal: true,
              },
              {
                label: 'Ideal Customer Profile',
                href: `${legacyAdminUrl}/org/${tenantId}/preferences/icp`,
                icon: faChartLine,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/preferences/icp'),
                isExternal: true,
              },
              {
                label: 'Profiles to Exclude',
                href: `${legacyAdminUrl}/org/${tenantId}/preferences/anti-icp`,
                icon: faChartBar,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/preferences/anti-icp'),
                isExternal: true,
              },
              {
                label: 'Competitors & Partners',
                href: `${legacyAdminUrl}/org/${tenantId}/preferences/competitors-partners`,
                icon: faBuilding,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/preferences/competitors') ||
                  path.includes('/preferences/competitors-partners'),
                isExternal: true,
              },
              {
                label: 'Key Website Pages',
                href: `${legacyAdminUrl}/org/${tenantId}/preferences/key-website-pages`,
                icon: faCog,
                visibleFor: ['admin', 'superKudu'] as Array<
                  'admin' | 'superKudu'
                >,
                activeMatcher: (path: string) =>
                  path.includes('/preferences/key-website-pages'),
                isExternal: true,
              },
            ]
          : []),
      ],
    },
    ...(isSuperKudu
      ? [
          {
            label: 'SUPERKUDU ADMIN',
            items: [
              {
                label: 'Admin',
                href: `/org/${tenantId}/settings/admin`,
                icon: faUserGear,
                visibleFor: ['superKudu'] as Array<'admin' | 'superKudu'>,
                activeMatcher: (path: string) =>
                  path.includes('/settings/admin'),
                isExternal: false,
              },
              {
                label: 'Flags',
                href: `/org/${tenantId}/settings/flags`,
                icon: faFlag,
                visibleFor: ['superKudu'] as Array<'admin' | 'superKudu'>,
                activeMatcher: (path: string) =>
                  path.includes('/settings/flags'),
                isExternal: false,
              },
              {
                label: 'YAML to SQL',
                href: `${legacyAdminUrl}/org/${tenantId}/settings/yaml-to-sql`,
                icon: faFlask,
                visibleFor: ['superKudu'] as Array<'admin' | 'superKudu'>,
                activeMatcher: (path: string) =>
                  path.includes('/settings/yaml-to-sql'),
                isExternal: true,
              },
            ],
          },
        ]
      : []),
  ];

  return {
    sections,
  };
}
